import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { isActiveLivePrompt, type LivePrompt } from '@/api/capture';
import { useT } from '@/i18n/runtime';
import { useLiveLeg, type AttachableTrack, type LiveLegDeps } from '@/media/useLiveLeg';
import { useCaptureStore } from '@/store/captureStore';
import { tenantFrom, tenantFromSummary, type TenantConfig } from '@/theme/tenant';
import { AppBar } from './components/AppBar';
import { Button } from './components/Button';
import { ConsentStep } from './ConsentStep';
import { Icon } from './components/Icon';
import { SecurePill } from './components/SecurePill';
import { CentredNotice, Shell } from './components/Shell';

/**
 * `/live` — "Join the live verification" (SV-5-2, FSD §2.3 J3): the representative's side of the officer-directed
 * leg. Waits honestly for an officer (the be says when one has taken the leg), then shows the officer, the
 * representative's own camera, and the officer's instruction prompts as they arrive: the ONE active prompt is an
 * instruction card (a heading by kind — pan / hold / walk / read — the officer's text, and a "Done" acknowledgement
 * that posts the answer); answered and lapsed prompts sit beneath as history. When the be says the live items still
 * need consent (409 SV_CONSENT_LIVE_REQUIRED on join) the ceremony runs here, then the join is asked again. Leaves
 * with a clear "the officer ended the session" state. DEC-073 words only: the METHOD (a recorded call with an
 * officer), never an outcome — a history row carries a check mark, never a verdict word.
 */
export function LiveLeg({ deps }: { deps?: LiveLegDeps } = {}): JSX.Element {
  const { t } = useT();
  const navigate = useNavigate();
  const summary = useCaptureStore((s) => s.summary);
  const tenant = tenantFromSummary(summary) ?? tenantFrom('');

  if (!summary) {
    return <Shell tenant={null}><CentredNotice title={t('sv.entry.linkRequired.title')} body={t('sv.entry.linkRequired.body')} /></Shell>;
  }
  return <LiveLegStep tenant={tenant} tenantName={summary.tenantName} deps={deps} onDone={() => navigate('/done', { replace: true })} />;
}

export function LiveLegStep({ tenant, tenantName, deps, onDone }: {
  tenant: TenantConfig;
  tenantName: string;
  deps?: LiveLegDeps | undefined;
  onDone: () => void;
}): JSX.Element {
  const { t, locale } = useT();
  const summary = useCaptureStore((s) => s.summary);
  const leg = useLiveLeg(true, deps);
  const { phase } = leg;

  if (phase.kind === 'consentRequired') {
    // The join is parked until the live items are consented to; the ceremony is the SAME component as the front
    // door's, fed the server's summary (its consentPurposes carry the three liveLeg rows), and its completion re-asks.
    return (
      <Shell tenant={tenant}>
        <div className="app-view" data-testid="live-leg" data-phase={phase.kind}>
          {summary ? (
            <>
              <p className="sub-sm sv-live-consent-lead" role="note" data-testid="live-consent-lead" style={{ margin: '12px 0 0' }}>
                <Icon name="info" size={14} /> {t('sv.live.consent.lead')}
              </p>
              <ConsentStep tenant={tenant} summary={summary} onDone={leg.consentRecorded} />
            </>
          ) : (
            <CentredNotice title={t('sv.entry.linkRequired.title')} body={t('sv.entry.linkRequired.body')} />
          )}
        </div>
      </Shell>
    );
  }

  const active = leg.prompts.find(isActiveLivePrompt) ?? null;
  const history = leg.prompts.filter((p) => !isActiveLivePrompt(p));

  const pill = phase.kind === 'live'
    ? <SecurePill kind="secure" label={t('sv.live.pill.recording')} />
    : <SecurePill kind="secure" label={t('sv.pill.secure')} />;

  return (
    <Shell tenant={tenant}>
      <div className="app-view" data-testid="live-leg" data-phase={phase.kind}>
        <AppBar tenant={tenant} subtitle={t('sv.subtitle')} right={pill} />
        <div className="stack">
          {phase.kind === 'waiting' || phase.kind === 'connecting' ? (
            <>
              <div>
                <div className="h-title sm" data-testid="live-title">{t('sv.live.title')}</div>
                <p className="sub-sm" style={{ marginTop: 6 }}>{t('sv.live.sub', { tenantName })}</p>
              </div>
              <div className="card pad" data-testid="live-waiting">
                <div className="app-preparing" role="status">
                  {phase.kind === 'waiting' ? t('sv.live.waiting.officer') : t('sv.live.connecting')}
                </div>
                <p className="pd" style={{ marginTop: 8 }}>{t('sv.live.waiting.body')}</p>
                <p className="sub-sm" style={{ marginTop: 8 }}>{t('sv.live.waiting.keepOpen')}</p>
              </div>
              <div className="card pad">
                <div className="pt">{t('sv.live.expect.title')}</div>
                <ul className="sv-capture-list">
                  <li>{t('sv.live.expect.officer')}</li>
                  <li>{t('sv.live.expect.prompts')}</li>
                  <li>{t('sv.live.expect.recorded', { tenantName })}</li>
                </ul>
              </div>
              <Button variant="ghost" onClick={leg.leave} data-testid="live-leave-waiting">{t('sv.live.action.leave')}</Button>
            </>
          ) : null}

          {phase.kind === 'live' ? (
            <>
              <div className="viewport wide" data-testid="live-viewport">
                <RemoteVideo track={leg.remoteVideoTrack} />
                <RemoteAudio track={leg.remoteAudioTrack} />
                <LocalPreview track={leg.localVideoTrack} />
                <div className="vp-hint" data-testid="live-status">
                  {phase.reconnecting
                    ? t('sv.live.reconnecting')
                    : leg.officerPresent
                      ? t('sv.live.officerHere')
                      : t('sv.live.officerJoining')}
                </div>
              </div>
              {leg.mediaErrorKey ? (
                <div className="reject-note" role="alert" data-testid="live-media-error">
                  {leg.mediaErrorKey === 'permission' ? t('sv.live.media.permission') : t('sv.live.media.other')}
                </div>
              ) : null}
              <div className="card pad" data-testid="live-prompts">
                <div className="pt">{t('sv.live.prompts.title')}</div>
                {active ? (
                  <div
                    className="sv-live-prompt"
                    role="region"
                    aria-live="polite"
                    data-testid="live-prompt-card"
                    data-kind={active.kind}
                    data-prompt-id={active.promptId}
                    data-state={active.state}
                    style={{ marginTop: 8 }}
                  >
                    <div className="h-title sm" data-testid="live-prompt-heading">{kindHeading(t, active.kind)}</div>
                    <p className="pd" style={{ marginTop: 6 }} data-testid="live-prompt-text">{promptText(active, locale)}</p>
                    <Button
                      onClick={() => void leg.answer(active.promptId)}
                      disabled={leg.answering === active.promptId}
                      data-testid="live-prompt-done"
                      style={{ marginTop: 10 }}
                    >
                      {t('sv.live.prompt.done')}
                    </Button>
                  </div>
                ) : null}
                {history.length > 0 ? (
                  <div style={{ marginTop: active ? 12 : 4 }}>
                    <div className="sub-sm">{t('sv.live.prompt.history')}</div>
                    <ul className="sv-capture-list" data-testid="live-prompt-history" style={{ marginTop: 4, listStyle: 'none', paddingInlineStart: 0 }}>
                      {history.map((p) => (
                        <li key={p.promptId} data-testid={`live-prompt-${p.promptId}`} data-state={p.state}>
                          {p.state === 'COMPLETED' ? (
                            <span aria-hidden="true" data-testid="live-prompt-check"><Icon name="check" size={14} /></span>
                          ) : null}{' '}
                          {promptText(p, locale)}{' '}
                          <span className="sub-sm">{stateWord(t, p.state)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {!active && history.length === 0 ? (
                  <p className="pd" style={{ marginTop: 4 }} data-testid="live-prompts-none">{t('sv.live.prompts.none')}</p>
                ) : null}
              </div>
              <Button variant="danger-soft" onClick={leg.leave} data-testid="live-leave">{t('sv.live.action.hangUp')}</Button>
            </>
          ) : null}

          {phase.kind === 'ended' ? (
            <div className="center-col" style={{ marginTop: 8 }} data-testid="live-ended">
              <span className="success-mark"><Icon name="check" size={28} /></span>
              <div className="h-title sm">{t('sv.live.ended.title')}</div>
              <p className="sub" style={{ marginTop: 6 }}>{t('sv.live.ended.body', { tenantName })}</p>
              <Button onClick={onDone} data-testid="live-done" style={{ marginTop: 16 }}>{t('sv.action.continue')}</Button>
            </div>
          ) : null}

          {phase.kind === 'left' ? (
            <div className="center-col" style={{ marginTop: 8 }} data-testid="live-left">
              <div className="h-title sm">{t('sv.live.left.title')}</div>
              <p className="sub" style={{ marginTop: 6 }}>{t('sv.live.left.body')}</p>
              <Button onClick={leg.retry} data-testid="live-rejoin" style={{ marginTop: 16 }}>{t('sv.live.action.rejoin')}</Button>
            </div>
          ) : null}

          {phase.kind === 'unavailable' ? (
            <CentredNotice title={t('sv.live.unavailable.title')} body={t('sv.live.unavailable.body')} action={<Button onClick={onDone}>{t('sv.action.continue')}</Button>} />
          ) : null}
          {phase.kind === 'superseded' ? (
            <CentredNotice title={t('sv.live.superseded.title')} body={t('sv.live.superseded.body')} />
          ) : null}
          {phase.kind === 'unreachable' ? (
            <CentredNotice
              title={t('sv.live.unreachable.title')}
              body={t('sv.live.unreachable.body')}
              action={<Button onClick={leg.retry} data-testid="live-retry">{t('sv.action.tryAgain')}</Button>}
            />
          ) : null}
        </div>
      </div>
    </Shell>
  );
}

type Translate = (key: string, vars?: Record<string, string>) => string;

/** The instruction heading by kind — the four the bank issues; an unknown (newer-bank) kind gets the neutral one. */
function kindHeading(t: Translate, kind: string): string {
  const key = `sv.live.prompt.kind.${kind}`;
  const v = t(key);
  return v === key ? t('sv.live.prompt.kind.OTHER') : v;
}

/** The be resolves `text` to the representative's locale at issue time; a switch to Hindi on this device follows
 *  `textHi` when the be sent it (non-empty), else the card stays on `text`. */
export function promptText(p: LivePrompt, locale: string): string {
  if (locale === 'hi' && typeof p.textHi === 'string' && p.textHi.trim() !== '') return p.textHi;
  return p.text;
}

/** A history row's state, in method words — what happened to the request, never a verdict on the answer. */
function stateWord(t: Translate, state: string): string {
  const key = `sv.live.prompt.state.${state}`;
  const v = t(key);
  return v === key ? '' : v;
}

function useAttach<T extends HTMLMediaElement>(track: AttachableTrack | null): React.RefObject<T> {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !track) return undefined;
    track.attach(el);
    return () => {
      track.detach(el);
    };
  }, [track]);
  return ref;
}

function RemoteVideo({ track }: { track: AttachableTrack | null }): JSX.Element {
  const ref = useAttach<HTMLVideoElement>(track);
  return <video ref={ref} className="vp-scene" autoPlay playsInline data-testid="live-remote-video" data-attached={track ? 'true' : 'false'} />;
}

function RemoteAudio({ track }: { track: AttachableTrack | null }): JSX.Element {
  const ref = useAttach<HTMLAudioElement>(track);
  return <audio ref={ref} autoPlay data-testid="live-remote-audio" />;
}

function LocalPreview({ track }: { track: AttachableTrack | null }): JSX.Element {
  const ref = useAttach<HTMLVideoElement>(track);
  return (
    <video
      ref={ref}
      className="sv-live-self"
      autoPlay
      playsInline
      muted
      data-testid="live-local-video"
      data-attached={track ? 'true' : 'false'}
    />
  );
}
